import { FormEvent, useEffect, useState } from "react";
import {
  AdminEntry,
  EmailAddressesService,
  OrgEmailAddresses,
} from "client/openapi";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import { errorText } from "components/Forms/formsApi";
import { COMPACT_INPUT, FieldError } from "components/Inputs/compact";
import CopyLinkRow from "components/PublicBooking/CopyLinkRow";
import Notifications from "util/notifications";
import { forgetPersonalAddresses } from "util/personalAddress";
import {
  GROUP_LABEL,
  SECTION,
  SECTION_TEXT,
  SECTION_TITLE,
} from "./settingsStyles";

const NOT_SAVED = "Not saved. Check your connection and try again.";

// A row: the address and what it is, then its buttons (under it on a phone).
const LINE =
  "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4";
const STATUS = "mt-0.5 text-xs text-gray-600";

// How long Undo shows after Remove, as for the app's undo notices.
const UNDO_FOR_MS = 8000;

/**
 * Organization Settings > Messaging: the organization's shared office
 * addresses, whose mail every admin sees, every admin with their personal
 * address, and where mailboxes forward. Any admin sets, changes, resends
 * or removes any admin's personal address here; only its admin can confirm
 * it. It loads the first time `wanted` is true, and saves each change on
 * its own (the dialog's Save isn't needed). Undo shows in the section after
 * Remove: a notice's Undo can't be clicked while this dialog is open, and
 * clicking outside it closes it.
 */
export default function EmailAddressSettings({
  orgId,
  wanted,
}: {
  orgId: number;
  wanted: boolean;
}) {
  const [addresses, setAddresses] = useState<OrgEmailAddresses>();
  const [loadFailed, setLoadFailed] = useState(false);
  const [newAddress, setNewAddress] = useState("");
  const [busy, setBusy] = useState(false);
  // What went wrong adding (under the box) or with one address (under it).
  const [error, setError] = useState<string>();
  const [rowError, setRowError] = useState<{ id: number; text: string }>();
  // The address just removed, while it can be put back.
  const [removed, setRemoved] = useState<{
    id: number;
    email: string;
    personal: boolean;
  }>();
  useEffect(() => {
    if (!removed) return;
    const timer = setTimeout(() => setRemoved(undefined), UNDO_FOR_MS);
    return () => clearTimeout(timer);
  }, [removed]);

  // Another organization's addresses load afresh.
  useEffect(() => {
    setAddresses(undefined);
    setLoadFailed(false);
    setError(undefined);
    setRowError(undefined);
    setRemoved(undefined);
  }, [orgId]);

  const loaded = addresses !== undefined;
  useEffect(() => {
    if (!wanted || loaded) return;
    let current = true;
    EmailAddressesService.getOfficeAddresses({ orgId })
      .then((found) => current && setAddresses(found))
      .catch(() => current && setLoadFailed(true));
    return () => {
      current = false;
    };
  }, [orgId, wanted, loaded]);

  const add = (e: FormEvent) => {
    e.preventDefault();
    if (!newAddress.trim() || busy) return;
    setBusy(true);
    setError(undefined);
    EmailAddressesService.addOfficeAddress({
      orgId,
      requestBody: { email: newAddress.trim() },
    })
      .then((found) => {
        setAddresses(found);
        setNewAddress("");
      })
      .catch((err) => setError(errorText(err, NOT_SAVED)))
      .finally(() => setBusy(false));
  };

  const remove = (
    addressId: number,
    email: string,
    personal: boolean,
    canUndo: boolean
  ) => {
    setBusy(true);
    setRowError(undefined);
    EmailAddressesService.removeAddress({ orgId, addressId })
      .then((found) => {
        setAddresses(found);
        setRemoved(canUndo ? { id: addressId, email, personal } : undefined);
      })
      .catch((err) =>
        setRowError({ id: addressId, text: errorText(err, NOT_SAVED) })
      )
      .finally(() => setBusy(false));
  };

  const undo = () => {
    if (!removed) return;
    setBusy(true);
    EmailAddressesService.restoreAddress({ orgId, addressId: removed.id })
      .then((found) => {
        setAddresses(found);
        setRemoved(undefined);
      })
      .catch((err) => Notifications.error(errorText(err, NOT_SAVED)))
      .finally(() => setBusy(false));
  };

  const resend = (addressId: number) => {
    setBusy(true);
    setRowError(undefined);
    EmailAddressesService.resendOfficeEmail({ orgId, addressId })
      .then((found) => {
        setAddresses(found);
        Notifications.success("We sent the email again.");
      })
      .catch((err) =>
        setRowError({ id: addressId, text: errorText(err, NOT_SAVED) })
      )
      .finally(() => setBusy(false));
  };

  const removeButton = (
    addressId: number,
    email: string,
    personal: boolean,
    canUndo = true
  ) => (
    <Button
      size={ButtonSize.EXTRA_SMALL}
      color={ButtonColor.RED}
      fill={ButtonFill.HOLLOW}
      aria-label={`Remove ${email}`}
      disabled={busy}
      onClick={() => remove(addressId, email, personal, canUndo)}
    >
      Remove
    </Button>
  );

  // Undo, under the list the address was removed from.
  const undoLine = (personal: boolean) =>
    removed?.personal === personal && (
      <p
        role="status"
        className="mt-1 flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm"
      >
        Removed {removed.email}.
        <button
          type="button"
          className="font-semibold text-[color:var(--green)] hover:underline disabled:opacity-50"
          disabled={busy}
          onClick={undo}
        >
          Undo
        </button>
      </p>
    );

  // The admin whose personal address is being typed, and what's typed.
  const [editing, setEditing] = useState<{
    accountId: string;
    email: string;
  }>();
  const [adminError, setAdminError] = useState<{
    accountId: string;
    text: string;
  }>();

  // Your own address changed: reply boxes look it up again.
  const settled = (found: OrgEmailAddresses, admin: AdminEntry) => {
    setAddresses(found);
    if (admin.yours) forgetPersonalAddresses();
  };

  const savePersonal = (e: FormEvent, admin: AdminEntry) => {
    e.preventDefault();
    if (!editing || busy) return;
    setBusy(true);
    setAdminError(undefined);
    EmailAddressesService.setAdminPersonalAddress({
      orgId,
      accountId: admin.account_id,
      requestBody: { email: editing.email.trim() || null },
    })
      .then((found) => {
        settled(found, admin);
        setEditing(undefined);
      })
      .catch((err) =>
        setAdminError({
          accountId: admin.account_id,
          text: errorText(err, NOT_SAVED),
        })
      )
      .finally(() => setBusy(false));
  };

  const resendPersonal = (admin: AdminEntry) => {
    setBusy(true);
    setAdminError(undefined);
    EmailAddressesService.resendAdminPersonalLink({
      orgId,
      accountId: admin.account_id,
    })
      .then((found) => {
        settled(found, admin);
        Notifications.success(`We sent a new link to ${admin.address?.email}.`);
      })
      .catch((err) =>
        setAdminError({
          accountId: admin.account_id,
          text: errorText(err, NOT_SAVED),
        })
      )
      .finally(() => setBusy(false));
  };

  const adminStatus = (admin: AdminEntry) => {
    const address = admin.address;
    if (!address) return "No personal address.";
    const who = admin.yours ? "you" : admin.name;
    return address.confirmed
      ? `Confirmed. Its mail shows only to ${who}.`
      : admin.yours
      ? "Waiting for you to open the link Tadpole emailed to it."
      : `Waiting for ${admin.name} to open the link Tadpole emailed to it, signed in as themselves.`;
  };

  const formerAddresses = (addresses?.personal_addresses ?? []).filter(
    (address) => !address.still_admin
  );

  const errorFor = (addressId: number) =>
    rowError?.id === addressId && (
      <FieldError id={`address-${addressId}-error`}>{rowError.text}</FieldError>
    );

  return (
    <section className={`${SECTION} mt-4 max-w-3xl`}>
      <h3 className={SECTION_TITLE}>Email addresses</h3>
      <p className={SECTION_TEXT}>
        Mail that comes in to an office address shows in Communication for every
        admin. Each admin can also have a personal address, and its mail shows
        only to them.
      </p>

      {loadFailed ? (
        <p className="mt-4 text-sm text-red-600">
          Couldn&apos;t load this. Close Organization Settings and open it
          again.
        </p>
      ) : !addresses ? (
        <div className="mt-4 h-24 animate-pulse rounded-md bg-gray-100" />
      ) : !addresses.main_address ? (
        <p className="mt-4 text-sm text-gray-700">
          Tadpole hasn&apos;t set up email for your organization yet. Contact
          Tadpole support to set up your office address.
        </p>
      ) : (
        <>
          <p className={GROUP_LABEL}>Office addresses</p>
          <p className={SECTION_TEXT}>
            Adding an address here doesn&apos;t change where Tadpole sends from.
            It tells Tadpole the address is the office&apos;s: an email with it
            in To or Cc shows to every admin, even when it also reaches an
            admin&apos;s personal address, and copies of mail sent from it show
            to every admin. Tadpole sends each new address an email, and the
            address counts once that email comes back through its forward.
            Adding and removing save right away.
          </p>
          <ul className="mt-2 divide-y divide-gray-100 text-sm">
            <li className={`${LINE} py-2`}>
              <div className="min-w-0">
                <p className="truncate font-medium">{addresses.main_address}</p>
                <p className={STATUS}>
                  Main address. Tadpole sends your organization&apos;s email
                  from it, except the one-to-one emails and replies that admins
                  send from their own confirmed addresses. To change it, contact
                  Tadpole support.
                </p>
              </div>
            </li>
            {addresses.office_addresses.map((address) => (
              <li key={address.id} className="py-2">
                <div className={LINE}>
                  <div className="min-w-0">
                    <p className="truncate">{address.email}</p>
                    <p className={STATUS}>
                      {address.confirmed
                        ? "Confirmed."
                        : "Waiting for Tadpole's email to come back. Once this mailbox forwards to the address below, send the email again."}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {!address.confirmed && (
                      <Button
                        size={ButtonSize.EXTRA_SMALL}
                        color={ButtonColor.GREEN}
                        fill={ButtonFill.HOLLOW}
                        disabled={busy}
                        onClick={() => resend(address.id)}
                      >
                        Send the email again
                      </Button>
                    )}
                    {removeButton(address.id, address.email, false)}
                  </div>
                </div>
                {errorFor(address.id)}
              </li>
            ))}
          </ul>
          {undoLine(false)}

          <form onSubmit={add} className="mt-2 flex items-center gap-2">
            <label htmlFor="new-office-address" className="sr-only">
              Another office address
            </label>
            <input
              id="new-office-address"
              type="email"
              maxLength={255}
              placeholder={`name@${addresses.sending_domain}`}
              className={`${COMPACT_INPUT} w-full max-w-sm`}
              value={newAddress}
              aria-invalid={!!error}
              aria-describedby={error ? "office-address-error" : undefined}
              onChange={(e) => setNewAddress(e.target.value)}
            />
            <Button
              type="submit"
              size={ButtonSize.SMALL}
              color={
                newAddress.trim() && !busy
                  ? ButtonColor.GREEN
                  : ButtonColor.GRAY
              }
              fill={ButtonFill.HOLLOW}
              disabled={!newAddress.trim() || busy}
              extraClasses="shrink-0"
            >
              Add address
            </Button>
          </form>
          {error && <FieldError id="office-address-error">{error}</FieldError>}

          <p className={GROUP_LABEL}>Setting up your mailboxes</p>
          <p className="mt-1 text-sm text-gray-700">
            Have each office mailbox forward its mail to this address:
          </p>
          {addresses.forward_to && (
            <CopyLinkRow
              url={addresses.forward_to}
              open={false}
              copied="Address copied"
            />
          )}
          <p className="mt-3 text-sm text-gray-700">
            To keep a record of what your staff send from their own mailboxes,
            have your email provider copy their sent mail to this address:
          </p>
          {addresses.copy_to && (
            <CopyLinkRow
              url={addresses.copy_to}
              open={false}
              copied="Address copied"
            />
          )}

          <p className={GROUP_LABEL}>Admins&apos; personal addresses</p>
          <p className={SECTION_TEXT}>
            Any admin can add or change an admin&apos;s personal address.
            Tadpole emails it a link, and the address counts once its admin
            opens the link while signed in as themselves. Its mail shows only to
            that admin, whoever added it.
          </p>
          <ul className="mt-1 divide-y divide-gray-100 text-sm">
            {(addresses.admins ?? []).map((admin) => {
              const address = admin.address;
              const isEditing = editing?.accountId === admin.account_id;
              return (
                <li key={admin.account_id} className="py-2">
                  {isEditing || !address ? (
                    <form
                      onSubmit={(e) => savePersonal(e, admin)}
                      className={LINE}
                    >
                      <div className="min-w-0 flex-1">
                        <label
                          htmlFor={`personal-${admin.account_id}`}
                          className="font-medium"
                        >
                          {admin.name}
                          {admin.yours && " (you)"}
                        </label>
                        <input
                          id={`personal-${admin.account_id}`}
                          type="email"
                          maxLength={255}
                          placeholder={`name@${addresses.sending_domain}`}
                          className={`${COMPACT_INPUT} mt-1 w-full max-w-sm`}
                          value={isEditing ? editing.email : ""}
                          aria-invalid={
                            adminError?.accountId === admin.account_id
                          }
                          onChange={(e) =>
                            setEditing({
                              accountId: admin.account_id,
                              email: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Button
                          type="submit"
                          size={ButtonSize.EXTRA_SMALL}
                          color={ButtonColor.GREEN}
                          fill={ButtonFill.HOLLOW}
                          disabled={
                            busy ||
                            !isEditing ||
                            (!address && !editing.email.trim())
                          }
                        >
                          {address ? "Save" : "Add"}
                        </Button>
                        {isEditing && (
                          <Button
                            size={ButtonSize.EXTRA_SMALL}
                            color={ButtonColor.GRAY}
                            fill={ButtonFill.HOLLOW}
                            disabled={busy}
                            onClick={() => {
                              setEditing(undefined);
                              setAdminError(undefined);
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </form>
                  ) : (
                    <div className={LINE}>
                      <div className="min-w-0">
                        <p className="truncate">
                          <span className="font-medium">
                            {admin.name}
                            {admin.yours && " (you)"}
                          </span>{" "}
                          · {address.email}
                        </p>
                        <p className={STATUS}>{adminStatus(admin)}</p>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        {!address.confirmed && (
                          <Button
                            size={ButtonSize.EXTRA_SMALL}
                            color={ButtonColor.GREEN}
                            fill={ButtonFill.HOLLOW}
                            disabled={busy}
                            onClick={() => resendPersonal(admin)}
                          >
                            Send the link again
                          </Button>
                        )}
                        <Button
                          size={ButtonSize.EXTRA_SMALL}
                          color={ButtonColor.SKYBLUE}
                          fill={ButtonFill.HOLLOW}
                          disabled={busy}
                          onClick={() => {
                            setAdminError(undefined);
                            setEditing({
                              accountId: admin.account_id,
                              email: address.email,
                            });
                          }}
                        >
                          Change
                        </Button>
                        {removeButton(address.id, address.email, true)}
                      </div>
                    </div>
                  )}
                  {address?.confirmed && address.forward_to && !isEditing && (
                    <div className="mt-1">
                      <p className={STATUS}>
                        Have this mailbox forward its mail to this address:
                      </p>
                      <CopyLinkRow
                        url={address.forward_to}
                        open={false}
                        copied="Address copied"
                      />
                    </div>
                  )}
                  {adminError?.accountId === admin.account_id && (
                    <FieldError id={`personal-${admin.account_id}-error`}>
                      {adminError.text}
                    </FieldError>
                  )}
                  {address && errorFor(address.id)}
                </li>
              );
            })}
          </ul>
          {formerAddresses.length > 0 && (
            <>
              <p className={GROUP_LABEL}>Former admins&apos; addresses</p>
              <ul className="mt-1 divide-y divide-gray-100 text-sm">
                {formerAddresses.map((address) => (
                  <li key={address.id} className="py-2">
                    <div className={LINE}>
                      <div className="min-w-0">
                        <p className="truncate">
                          {address.name} · {address.email}
                        </p>
                        <p className={STATUS}>
                          No longer an admin, so Tadpole doesn&apos;t use this
                          address.
                        </p>
                      </div>
                      {removeButton(address.id, address.email, true, false)}
                    </div>
                    {errorFor(address.id)}
                  </li>
                ))}
              </ul>
            </>
          )}
          {undoLine(true)}
        </>
      )}
    </section>
  );
}
